import { useCallback, useEffect, useRef } from 'react'

type UseMediaSessionProps = {
  title: string | null
  artist?: string
  album?: string
  artworkUrl?: string
  audioRef: React.RefObject<HTMLAudioElement | null>
  onPreviousTrack?: () => void
  onNextTrack?: () => void
}

// timeupdate は毎秒 4 回程度発火する。iOS は setPositionState を高頻度で受けると
// ロック画面のスクラバーが不安定になるため間引く
const POSITION_STATE_UPDATE_INTERVAL_MS = 1000

// OS がスキップ幅を渡してこない場合だけ使う既定値（アプリ内の ±30 秒ボタンに合わせる）
const DEFAULT_SEEK_OFFSET_SEC = 30

// ロック画面のスキップボタンに表示される秒数は OS が決めており、その値が seekOffset で渡ってくる。
// 独自の値でシークするとアイコンの表示（iOS では 10 秒）と実際の移動量が食い違うため OS 側に従う
const resolveSeekOffset = (details: MediaSessionActionDetails): number => {
  const offset = details.seekOffset
  return offset != null && Number.isFinite(offset) && offset > 0 ? offset : DEFAULT_SEEK_OFFSET_SEC
}

/**
 * Media Session API を使ってOS通知 / ロック画面にメディアコントロールを表示し、
 * バックグラウンド再生を有効にする汎用フック
 */
export const useMediaSession = ({
  title,
  artist,
  album,
  artworkUrl,
  audioRef,
  onPreviousTrack,
  onNextTrack,
}: UseMediaSessionProps) => {
  // メタデータの設定
  useEffect(() => {
    if (!('mediaSession' in navigator) || !title) return

    const artwork: MediaImage[] = artworkUrl ? [{ src: artworkUrl, sizes: '512x512', type: 'image/webp' }] : []

    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: artist ?? undefined,
      album: album ?? undefined,
      artwork,
    })
  }, [title, artist, album, artworkUrl])

  const lastPositionStateAtRef = useRef(0)

  // 再生位置の更新
  // OS は「最後に通知した position + playbackRate × 経過時間」でロック画面の位置を外挿する。
  // 通知が滞ると外挿だけが進み、再生ボタンを押したときに OS 側の位置へ引きずられて
  // 意図しない場所から再生されるため、停止・シーク時も必ず実位置を通知し直す
  const updatePositionState = useCallback(() => {
    if (!('mediaSession' in navigator) || !audioRef.current) return
    const audio = audioRef.current

    if (!audio.duration || !Number.isFinite(audio.duration)) return

    lastPositionStateAtRef.current = Date.now()
    try {
      navigator.mediaSession.setPositionState({
        duration: audio.duration,
        // 停止中に再生時の速度を渡すと OS がその速度で外挿し続けるため 1 に戻す
        // （仕様上 0 は指定できない）
        playbackRate: audio.paused ? 1 : audio.playbackRate,
        position: Math.min(Math.max(audio.currentTime, 0), audio.duration),
      })
    } catch {
      // Safari等で未サポートの場合は無視
    }
  }, [audioRef])

  // アクションハンドラの登録
  useEffect(() => {
    if (!('mediaSession' in navigator)) return

    const audio = audioRef.current

    // 前回 tryPlay が canplay を待っている間に再度 tryPlay が呼ばれると
    // リスナーが多重登録されるため、保留中のハンドラを記憶して付け替える
    let pendingCanPlayHandler: EventListener | null = null

    const tryPlay = async () => {
      if (!audio) return
      // OS 側の外挿位置ではなく要素の実位置から再生させるため、play の前に位置を通知し直す
      updatePositionState()
      try {
        await audio.play()
        navigator.mediaSession.playbackState = 'playing'
        updatePositionState()
      } catch (err) {
        // iOS でバックグラウンド中断後に NotAllowedError / AbortError が出ることがある。
        // 一度 load() で要素をリセットしてからリトライする
        console.warn('[MediaSession] play failed, retrying after load():', err)
        try {
          const rawTime = audio.currentTime
          const resumeAt = Number.isFinite(rawTime) && rawTime > 0 ? rawTime : 0
          audio.load()
          if (pendingCanPlayHandler) {
            audio.removeEventListener('canplay', pendingCanPlayHandler)
            pendingCanPlayHandler = null
          }
          const onCanPlay = async () => {
            pendingCanPlayHandler = null
            try {
              if (resumeAt > 0) audio.currentTime = resumeAt
              await audio.play()
              navigator.mediaSession.playbackState = 'playing'
              updatePositionState()
            } catch (retryErr) {
              console.error('[MediaSession] play retry failed:', retryErr)
            }
          }
          pendingCanPlayHandler = onCanPlay
          audio.addEventListener('canplay', onCanPlay, { once: true })
        } catch (retryErr) {
          console.error('[MediaSession] load() failed:', retryErr)
        }
      }
    }

    const handlers: [MediaSessionAction, MediaSessionActionHandler | null][] = [
      [
        'play',
        () => {
          void tryPlay()
        },
      ],
      [
        'pause',
        () => {
          // pause 操作時に保留中の canplay リトライがあれば解除する
          // (tryPlay 失敗後の canplay 待ちでユーザーが pause した場合、
          //  canplay 発火で意図せず再生再開されるのを防ぐ)
          if (pendingCanPlayHandler && audio) {
            audio.removeEventListener('canplay', pendingCanPlayHandler)
            pendingCanPlayHandler = null
          }
          audio?.pause()
          navigator.mediaSession.playbackState = 'paused'
          // 停止位置を確定させないと OS のスクラバーが進み続け、次の再生でそこへ飛ばされる
          updatePositionState()
        },
      ],
      ['previoustrack', onPreviousTrack ?? null],
      ['nexttrack', onNextTrack ?? null],
      [
        'seekbackward',
        (details) => {
          if (!audio) return
          audio.currentTime = Math.max(audio.currentTime - resolveSeekOffset(details), 0)
          updatePositionState()
        },
      ],
      [
        'seekforward',
        (details) => {
          if (!audio) return
          audio.currentTime = Math.min(audio.currentTime + resolveSeekOffset(details), audio.duration || 0)
          updatePositionState()
        },
      ],
      [
        'seekto',
        (details) => {
          if (!audio || details.seekTime == null) return
          if (!audio.duration || !Number.isFinite(audio.duration)) return
          audio.currentTime = Math.min(Math.max(details.seekTime, 0), audio.duration)
          updatePositionState()
        },
      ],
    ]

    for (const [action, handler] of handlers) {
      try {
        navigator.mediaSession.setActionHandler(action, handler)
      } catch {
        // 未サポートアクションは無視
      }
    }

    return () => {
      for (const [action] of handlers) {
        try {
          navigator.mediaSession.setActionHandler(action, null)
        } catch {
          // ignore
        }
      }
      if (pendingCanPlayHandler && audio) {
        audio.removeEventListener('canplay', pendingCanPlayHandler)
        pendingCanPlayHandler = null
      }
    }
  }, [audioRef, onPreviousTrack, onNextTrack, updatePositionState])

  // 再生状態と位置の同期
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !('mediaSession' in navigator)) return

    const onPlay = () => {
      navigator.mediaSession.playbackState = 'playing'
      updatePositionState()
    }
    const onPause = () => {
      navigator.mediaSession.playbackState = 'paused'
      updatePositionState()
    }
    const onSeeked = () => {
      updatePositionState()
    }
    const onRateChange = () => {
      updatePositionState()
    }
    const onTimeUpdate = () => {
      if (Date.now() - lastPositionStateAtRef.current < POSITION_STATE_UPDATE_INTERVAL_MS) return
      updatePositionState()
    }

    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('seeked', onSeeked)
    audio.addEventListener('ratechange', onRateChange)
    audio.addEventListener('timeupdate', onTimeUpdate)

    return () => {
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('seeked', onSeeked)
      audio.removeEventListener('ratechange', onRateChange)
      audio.removeEventListener('timeupdate', onTimeUpdate)
    }
  }, [audioRef, updatePositionState])
}
